import {
  Button,
  Center,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MessageThreadType, SearchFilterType, UserFragment } from "gql/graphql";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import UserAvatar from "modules/_shared/user-avatar";
import UserName from "modules/_shared/user-name";
import UserToggleStarredButton from "../drawers/components/user-profile/user-toggle-starred-button";
import NewChatButton from "modules/_shared/buttons/new-chat-button";
import NewMessageThreadButton from "modules/_shared/buttons/new-message-thread-button";
import {
  BuildingIcon,
  GroupsOutlineIcon,
  InfoIcon,
  PersonOutlineIcon,
} from "modules/icons";
import { useRouter } from "next/router";
import { useOverlay } from "modules/providers/overlay-provider";
import EmailAddress from "modules/_shared/email-address";
import UserAttributes from "../drawers/components/user-profile/user-attributes";
import { isEmpty } from "lodash";
import UserDistributionGroups from "../drawers/components/user-profile/user-distribution-groups";
import UserOnlineStatus from "../drawers/components/user-profile/user-online-status";
import UserInactive from "../drawers/components/user-profile/user-inactive";
import UserActive from "../drawers/components/user-profile/user-active";
import UserOutOfOfficeInfo from "modules/user-profile/user-out-of-office-info";
import useSearchFilter from "modules/hooks/use-search-filter";
import { SEARCH_PATH } from "modules/utils/Constants";
import { getUserMention, getE164PhoneNumber } from "modules/utils/Functions";
import NewTaskButton from "modules/_shared/buttons/new-task-button";
import VideoChatButton from "modules/_shared/buttons/video-chat-button";

interface IProps {
  user: UserFragment;
}

const UserProfile = ({ user }: IProps) => {
  const { closeDrawer } = useOverlay();
  const { push } = useRouter();
  const { getUrl } = useSearchFilter();

  const onViewChatsClicked = useCallback(() => {
    closeDrawer();
    push(
      getUrl(
        SEARCH_PATH,
        undefined,
        ...[
          {
            type: SearchFilterType.TypeList,
            stringListValue: [MessageThreadType.Chat],
          },
          {
            type: SearchFilterType.UserIdList,
            objectIdListValue: [user.id],
          },
        ],
      ),
    );
  }, [closeDrawer, getUrl, push, user]);

  const onViewThreadsClicked = useCallback(() => {
    closeDrawer();
    push(
      getUrl(
        SEARCH_PATH,
        undefined,
        ...[
          {
            type: SearchFilterType.TypeList,
            stringListValue: [MessageThreadType.Thread],
          },
          {
            type: SearchFilterType.UserIdList,
            objectIdListValue: [user.id],
          },
        ],
      ),
    );
  }, [closeDrawer, getUrl, push, user]);

  const onViewTasksClicked = useCallback(() => {
    closeDrawer();
    push(
      getUrl(
        SEARCH_PATH,
        undefined,
        ...[
          {
            type: SearchFilterType.TypeList,
            stringListValue: [MessageThreadType.Task],
          },
          {
            type: SearchFilterType.TaskAssigneeUserIdList,
            objectIdListValue: [user.id],
          },
        ],
      ),
    );
  }, [closeDrawer, getUrl, push, user]);

  const displayPhone = useMemo(
    () => !user.isHidePhone && !isEmpty(user.phone),
    [user.isHidePhone, user.phone],
  );

  const displayEmail = useMemo(
    () => !user.isHideEmail && !isEmpty(user.email),
    [user.email, user.isHideEmail],
  );

  const hideContactInfo = useMemo(
    () =>
      (!displayEmail || user.isHideEmail) &&
      (!displayPhone || user.isHidePhone),
    [displayEmail, displayPhone, user.isHideEmail, user.isHidePhone],
  );

  return (
    <DrawerContent>
      <DrawerCloseButton left={8} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontFamily="Outfit" fontSize={26} fontWeight={700}>
          User Profile
        </Center>
      </DrawerHeader>

      <DrawerBody>
        <Stack spacing={8} w="full">
          <HStack w="full" spacing={4}>
            <UserAvatar userId={user.id} size="xl" />
            <VStack w="full" alignItems="flex-start">
              <HStack w="full">
                <Text fontSize={26} fontWeight={700}>
                  <UserName userId={user.id} />
                </Text>
                <UserToggleStarredButton user={user} />
              </HStack>
              <UserOnlineStatus user={user} />
              <HStack pt={1} spacing={6}>
                <VideoChatButton
                  userIds={[user.id]}
                  circular
                  hasText
                  textAlignment="inline"
                />
                <NewTaskButton userId={user.id} />
                <NewChatButton userId={user.id} />
                <NewMessageThreadButton userId={user.id} />
              </HStack>
            </VStack>
          </HStack>
          <UserInactive user={user} />
          <UserActive user={user} />
          <UserOutOfOfficeInfo user={user} />
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <BuildingIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                Synco Info
              </Text>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Username
              </Text>
              <VStack w="full" alignItems="flex-start">
                <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                  {getUserMention(user)}
                </Text>
                <HStack w="full" spacing={4}>
                  <Button
                    variant="link"
                    color={colors.blue}
                    fontWeight={600}
                    fontSize={14}
                    onClick={onViewChatsClicked}
                  >
                    View Chats
                  </Button>
                  <Button
                    variant="link"
                    color={colors.blue}
                    fontWeight={600}
                    fontSize={14}
                    onClick={onViewThreadsClicked}
                  >
                    View Threads
                  </Button>
                  <Button
                    variant="link"
                    color={colors.blue}
                    fontWeight={600}
                    fontSize={14}
                    onClick={onViewTasksClicked}
                  >
                    View Tasks
                  </Button>
                </HStack>
              </VStack>
            </HStack>
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.gray0}
              py={2}
            >
              <Text
                minW={140}
                fontWeight={700}
                fontSize={14}
                color={colors.blackSecondary}
              >
                Synco Email
              </Text>
              <EmailAddress id={user.id} fontSize="14" truncate />
            </HStack>
          </VStack>
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <PersonOutlineIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                User Info
              </Text>
            </HStack>
            <UserAttributes user={user} />
          </VStack>
          {!hideContactInfo && (
            <VStack w="full" alignItems="flex-start">
              <HStack
                pb={2}
                w="full"
                borderBottomWidth={1}
                borderColor={colors.black}
              >
                <InfoIcon boxSize={6} fill={colors.blackSecondary} />
                <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                  Contact Info
                </Text>
              </HStack>
              {displayPhone && (
                <HStack
                  w="full"
                  borderBottomWidth={1}
                  borderBottomColor={colors.gray0}
                  py={2}
                >
                  <Text
                    minW={140}
                    fontWeight={700}
                    fontSize={14}
                    color={colors.blackSecondary}
                  >
                    Mobile
                  </Text>
                  <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                    {getE164PhoneNumber(user.phone)}
                  </Text>
                </HStack>
              )}
              {displayEmail && (
                <HStack
                  w="full"
                  borderBottomWidth={1}
                  borderBottomColor={colors.gray0}
                  py={2}
                >
                  <Text
                    minW={140}
                    fontWeight={700}
                    fontSize={14}
                    color={colors.blackSecondary}
                  >
                    Email
                  </Text>
                  <Text fontSize={14} color={colors.gray50} fontWeight={500}>
                    {user.email}
                  </Text>
                </HStack>
              )}
            </VStack>
          )}
          <VStack w="full" alignItems="flex-start">
            <HStack
              pb={2}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.black}
            >
              <GroupsOutlineIcon boxSize={6} fill={colors.blackSecondary} />
              <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
                Groups
              </Text>
            </HStack>
            <UserDistributionGroups user={user} />
          </VStack>
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(UserProfile);
